import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Card,
  Chip,
  Checkbox,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Send as SendIcon,
  ArrowForward as ArrowForwardIcon,
  Launch as LaunchIcon,
  CallMade as CallMadeIcon,
  TrendingFlat as TrendingFlatIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { exportToCsv } from './reports/ReportLayout';
import { materialApi, materialsApi, inventoryAdjustmentApi, inventoryChangeLogApi, AdjustmentType, InventoryAdjustment, InventoryChangeLog, CreateBulkAdjustmentDTO, BulkAdjustmentResult } from '../api/materials';
import api from '../services/api';
import { purchaseApi } from '../api/materials';
import { usePermissions } from '../hooks/usePermissions';
import BuildIcon from '@mui/icons-material/Build';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import designMaterialsApi, { CreateDesignMaterialDTO } from '../api/designMaterials';
import designApi from '../api/design';
import { GarmentPart, GARMENT_PARTS } from '../constants/garmentConstants';
import {
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  TableSortLabel
} from '@mui/material';

interface InventoryPageProps { }

const InventoryPage: React.FC<InventoryPageProps> = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user: currentUser } = usePermissions();
  const isMaster = currentUser?.role === 'MASTER';
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if we have a URL search param 'q' - this indicates external navigation (e.g., from DesignPreviewDialog)
  const urlSearchQuery = searchParams.get('q');

  // Inventory-related state variables
  const [inventory, setInventory] = useState<any[]>([]);
  const [inventoryTotal, setInventoryTotal] = useState(0);
  const [inventoryPage, setInventoryPage] = useState(1);
  const [inventoryPageSize, setInventoryPageSize] = useState(50);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  
  // Initialize search from URL param (priority) or localStorage
  const [inventorySearch, setInventorySearch] = useState<string>(() => {
    if (urlSearchQuery) return urlSearchQuery;
    return localStorage.getItem('inventorySearch') || '';
  });
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [showSamplesOnly, setShowSamplesOnly] = useState<boolean | null>(() => {
    // If URL param exists, reset filters to show all
    if (urlSearchQuery) return null;
    const saved = localStorage.getItem('inventoryShowSamplesOnly');
    if (saved === 'true') return true;
    if (saved === 'false') return false;
    return null;
  });
  const [selectedMaterial, setSelectedMaterial] = useState<any>(null);

  // Filter state variables - reset to defaults if URL param exists
  const [availableQtyOnly, setAvailableQtyOnly] = useState(() => {
    if (urlSearchQuery) return false; // Show all when searching from external link
    const saved = localStorage.getItem('inventoryAvailableQtyOnly');
    return saved !== null ? saved === 'true' : true;
  });
  const [unallocatedOnly, setUnallocatedOnly] = useState(() => {
    if (urlSearchQuery) return false; // Show all when searching from external link
    const saved = localStorage.getItem('inventoryUnallocatedOnly');
    return saved !== null ? saved === 'true' : true;
  });
  const [materialTypeFilter, setMaterialTypeFilter] = useState<string>(() => {
    if (urlSearchQuery) return ''; // Show all types when searching from external link
    return localStorage.getItem('inventoryMaterialTypeFilter') || 'FABRIC';
  });
  const [availableMaterialTypes, setAvailableMaterialTypes] = useState<string[]>([]);
  const [vendorFilter, setVendorFilter] = useState<number | null>(() => {
    if (urlSearchQuery) return null; // Clear vendor filter when searching from external link
    const saved = localStorage.getItem('inventoryVendorFilter');
    return saved ? parseInt(saved) : null;
  });
  const [availableVendors, setAvailableVendors] = useState<any[]>([]);

  // Persist search to localStorage
  useEffect(() => {
    localStorage.setItem('inventorySearch', inventorySearch);
  }, [inventorySearch]);

  // Persist filters to localStorage
  useEffect(() => {
    if (showSamplesOnly === null) {
      localStorage.setItem('inventoryShowSamplesOnly', 'null');
    } else {
      localStorage.setItem('inventoryShowSamplesOnly', showSamplesOnly.toString());
    }
  }, [showSamplesOnly]);

  useEffect(() => {
    localStorage.setItem('inventoryAvailableQtyOnly', availableQtyOnly.toString());
  }, [availableQtyOnly]);

  useEffect(() => {
    localStorage.setItem('inventoryUnallocatedOnly', unallocatedOnly.toString());
  }, [unallocatedOnly]);

  useEffect(() => {
    localStorage.setItem('inventoryMaterialTypeFilter', materialTypeFilter);
  }, [materialTypeFilter]);

  useEffect(() => {
    if (vendorFilter) {
      localStorage.setItem('inventoryVendorFilter', vendorFilter.toString());
    } else {
      localStorage.removeItem('inventoryVendorFilter');
    }
  }, [vendorFilter]);

  // Clear URL param after initial load so subsequent navigations work normally
  useEffect(() => {
    if (urlSearchQuery) {
      // Remove the 'q' param from URL without reloading
      searchParams.delete('q');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Dialog state variables
  const [previewDialogOpen, setPreviewDialogOpen] = useState<boolean>(false);
  const [allocationDialogOpen, setAllocationDialogOpen] = useState<boolean>(false);

  // Allocations state
  const [allocations, setAllocations] = useState<any[]>([]);
  const [allocationsLoading, setAllocationsLoading] = useState(false);

  // Material allocation dialog state
  const [selectedMaterialForAllocation, setSelectedMaterialForAllocation] = useState<any>(null);
  const [designs, setDesigns] = useState<any[]>([]);
  const [allocationFormData, setAllocationFormData] = useState<Partial<CreateDesignMaterialDTO>>({
    design_no: '',
    design_company_id: 0,
    material_id: 0,
    garment_part: '',
    is_sample: false,
    allocated_qty: 0
  });
  const [selectedDesignForAllocation, setSelectedDesignForAllocation] = useState<any>(null);
  const [allocationErrors, setAllocationErrors] = useState<{ [key: string]: string }>({});
  const [allocationLoading, setAllocationLoading] = useState(false);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [serverSummary, setServerSummary] = useState<any | null>(null);

  // Inventory adjustment state
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [selectedMaterialForAdjustment, setSelectedMaterialForAdjustment] = useState<any>(null);
  const [adjustmentFormData, setAdjustmentFormData] = useState({
    adjustment_type: AdjustmentType.STOCK_AUDIT,
    new_qty: 0,
    reason: '',
    reference_no: ''
  });
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [adjustmentHistory, setAdjustmentHistory] = useState<InventoryAdjustment[]>([]);
  const [adjustmentHistoryLoading, setAdjustmentHistoryLoading] = useState(false);
  const [changeLog, setChangeLog] = useState<InventoryChangeLog[]>([]);
  const [changeLogLoading, setChangeLogLoading] = useState(false);

  // ── Bulk selection & bulk adjustment ──────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkAdjustOpen, setBulkAdjustOpen] = useState(false);
  const [bulkAdjustType, setBulkAdjustType] = useState<AdjustmentType>(AdjustmentType.STOCK_AUDIT);
  const [bulkAdjustReason, setBulkAdjustReason] = useState('');
  const [bulkAdjustRef, setBulkAdjustRef] = useState('');
  const [bulkQtyMap, setBulkQtyMap] = useState<Record<number, string>>({});   // string for controlled inputs
  const [bulkAdjustLoading, setBulkAdjustLoading] = useState(false);
  const [bulkAdjustResult, setBulkAdjustResult] = useState<BulkAdjustmentResult | null>(null);

  // Image lightbox state
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Recalculate inventory state (MASTER only)
  const [recalcDialogOpen, setRecalcDialogOpen] = useState(false);
  const [recalcPreviewLoading, setRecalcPreviewLoading] = useState(false);
  const [recalcApplyLoading, setRecalcApplyLoading] = useState(false);
  const [recalcChanges, setRecalcChanges] = useState<any[]>([]);
  const [recalcDone, setRecalcDone] = useState(false);

  // View mode toggle: 'table' on desktop, 'card' on mobile — persisted to localStorage
  // isMobile is derived from theme breakpoints at component level
  const [viewMode, setViewMode] = useState<'card' | 'table'>(() => {
    const saved = localStorage.getItem('inventoryViewMode') as 'card' | 'table' | null;
    if (saved) return saved;
    // Default: table on desktop (md+), card on mobile
    return window.innerWidth >= 900 ? 'table' : 'card';
  });
  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: 'card' | 'table' | null) => {
    if (!newMode) return;
    setViewMode(newMode);
    localStorage.setItem('inventoryViewMode', newMode);
    // Reset to page 1 on mode switch so table fetches a clean first page
    setInventoryPage(1);
    setInventory([]);
    setHasMore(true);
  };

  // Table sort state
  type SortCol = 'material_code' | 'material_type' | 'material_quality' | 'quantity' | 'allocated_qty' | 'available_qty' | 'rate' | 'fabric_width' | 'last_purchase_date';
  const [tableSortCol, setTableSortCol] = useState<SortCol>('material_code');
  const [tableSortDir, setTableSortDir] = useState<'asc' | 'desc'>('asc');

  const handleTableSort = (col: SortCol) => {
    if (tableSortCol === col) {
      setTableSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setTableSortCol(col);
      setTableSortDir('asc');
    }
  };

  // Debounce search input to avoid fetching on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(inventorySearch);
    }, 400);
    return () => clearTimeout(handle);
  }, [inventorySearch]);

  // Reset to first page and clear items when filters/search change
  useEffect(() => {
    setInventory([]);
    setInventoryPage(1);
    setHasMore(true);
    setSelectedIds(new Set()); // clear bulk selection when filters change
  }, [debouncedSearch, vendorFilter, materialTypeFilter, showSamplesOnly, availableQtyOnly, unallocatedOnly]);

  // Fetch inventory on component mount and when page or filters change
  useEffect(() => {
    fetchInventory(inventoryPage, inventoryPageSize);
    fetchDesigns();
    fetchMaterialTypes();
    fetchVendors();
  }, [inventoryPage, inventoryPageSize, debouncedSearch, vendorFilter, materialTypeFilter, showSamplesOnly, availableQtyOnly, unallocatedOnly]);

  // Infinite scroll: card view only
  useEffect(() => {
    if (viewMode !== 'card') return;
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          setInventoryPage(prev => prev + 1);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [viewMode, hasMore, loading, loadingMore]);

  // Fetch available material types from database
  const fetchMaterialTypes = async () => {
    try {
      const types = await materialsApi.getDistinctTypes();
      setAvailableMaterialTypes(types);
    } catch (err: any) {
      console.error('Error fetching material types:', err);
    }
  };

  // Fetch available vendors (suppliers) from database
  const fetchVendors = async () => {
    try {
      const vendors = await materialsApi.getDistinctVendors();
      setAvailableVendors(vendors);
    } catch (err: any) {
      console.error('Error fetching vendors:', err);
    }
  };

  // Fetch inventory — page 1 replaces, subsequent pages append (infinite scroll)
  const fetchInventory = async (page = 1, pageSize = 50) => {
    try {
      // Table view: always a fresh replace. Card view: page 1 replaces, rest appends.
      if (page === 1 || viewMode === 'table') {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params: any = {
        page,
        pageSize,
        q: debouncedSearch || undefined
      };

      // Add vendor filter if selected
      if (vendorFilter) {
        params.vendor_id = vendorFilter;
      }
      // Add material type filter if selected (empty string means all)
      if (materialTypeFilter) {
        params.material_type = materialTypeFilter;
      }
      // Add sample filter if explicitly chosen
      if (showSamplesOnly !== null) {
        params.is_sample = showSamplesOnly;
      }
      // Add availability filter
      if (availableQtyOnly) {
        params.available_only = true;
      }
      // Add unallocated-only filter
      if (unallocatedOnly) {
        params.unallocated_only = true;
      }

      const response = await materialApi.getInventorySummary(params);

      if (response.data.data) {
        const { data, total, page: respPage, pageSize: respPageSize, metrics } = response.data;
        const processedInventory = data.map((item: any) => ({
          ...item,
          available_qty: item.quantity - (item.allocated_qty || 0)
        }));
        if (page === 1 || viewMode === 'table') {
          setInventory(processedInventory);
        } else {
          setInventory(prev => [...prev, ...processedInventory]);
        }
        setInventoryTotal(total);
        setInventoryPage(respPage);
        setHasMore(respPage * respPageSize < total);
        setServerSummary(metrics || null);
      } else {
        const processedInventory = response.data.map((item: any) => ({
          ...item,
          available_qty: item.quantity - (item.allocated_qty || 0)
        }));
        setInventory(processedInventory);
        setInventoryTotal(processedInventory.length);
        setInventoryPage(1);
        setHasMore(false);
        setServerSummary(null);
      }
    } catch (err: any) {
      console.error('Error fetching inventory:', err);
      setError('Failed to load inventory. Please try again later.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const getMaterialTypeName = (type: string) => {
    if (!type) return 'All Types';
    if (type === 'SEMI_FINISHED') return 'Semi Finished';
    // Convert to Title Case (FABRIC -> Fabric, LACE -> Lace, etc.)
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const handleDownloadCsv = async () => {
    try {
      setCsvLoading(true);
      const params: any = { page: 1, pageSize: 100000, q: debouncedSearch || undefined };
      if (vendorFilter) params.vendor_id = vendorFilter;
      if (materialTypeFilter) params.material_type = materialTypeFilter;
      if (showSamplesOnly !== null) params.is_sample = showSamplesOnly;
      if (availableQtyOnly) params.available_only = true;
      if (unallocatedOnly) params.unallocated_only = true;

      const response = await materialApi.getInventorySummary(params);
      const rawData = response.data.data || response.data;

      const rows = rawData.map((item: any) => ({
        'Code': item.material_code || '',
        'Type': getMaterialTypeName(item.material_type || ''),
        'Quality': item.material_quality || '',
        'Supplier': item.vendor?.alias || item.vendor?.registered_name || '',
        'UOM': item.uom || '',
        'Purchase Qty': item.quantity ?? 0,
        'Allocated': item.allocated_qty ?? 0,
        'Available': (item.quantity ?? 0) - (item.allocated_qty ?? 0),
        'Rate': item.rate ?? 0,
        'Width': item.fabric_width ?? '',
        'Last Purchase': item.last_purchase_date ? formatDate(item.last_purchase_date) : '',
        'Sample': item.is_sample ? 'Yes' : 'No',
      }));

      const today = format(new Date(), 'yyyy-MM-dd');
      exportToCsv(`inventory_export_${today}.csv`, rows);
    } catch (err) {
      console.error('CSV export failed:', err);
      setError('Failed to export CSV. Please try again.');
    } finally {
      setCsvLoading(false);
    }
  };

  // Update filteredInventory to apply filters and sort by latest purchase date
  const filteredInventory = inventory.filter((item) => {
    if (availableQtyOnly && (!item.available_qty || item.available_qty <= 0)) return false;
    if (unallocatedOnly && (item.available_qty <= 0)) return false;
    if (showSamplesOnly === true && !item.is_sample) return false;
    if (showSamplesOnly === false && item.is_sample) return false;
    if (materialTypeFilter && item.material_type !== materialTypeFilter) return false;

    if (!inventorySearch) return true;
    // When a search term is present, rely on backend filtering (q param)
    return true;
  }).sort((a, b) => {
    // Sort by created_at (latest first) when available, fallback to last_purchase_date
    const createdA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const createdB = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (createdA !== 0 || createdB !== 0) {
      return createdB - createdA;
    }
    const dateA = a.last_purchase_date ? new Date(a.last_purchase_date).getTime() : 0;
    const dateB = b.last_purchase_date ? new Date(b.last_purchase_date).getTime() : 0;
    return dateB - dateA;
  });

  const sortedInventory = useMemo(() => {
    const dir = tableSortDir === 'asc' ? 1 : -1;
    return [...filteredInventory].sort((a, b) => {
      let aVal: any;
      let bVal: any;
      switch (tableSortCol) {
        case 'material_code':     aVal = a.material_code || ''; bVal = b.material_code || ''; break;
        case 'material_type':     aVal = a.material_type || ''; bVal = b.material_type || ''; break;
        case 'material_quality':  aVal = a.material_quality || ''; bVal = b.material_quality || ''; break;
        case 'quantity':          aVal = a.quantity ?? 0; bVal = b.quantity ?? 0; break;
        case 'allocated_qty':     aVal = a.allocated_qty ?? 0; bVal = b.allocated_qty ?? 0; break;
        case 'available_qty':     aVal = a.available_qty ?? 0; bVal = b.available_qty ?? 0; break;
        case 'rate':              aVal = a.rate ?? 0; bVal = b.rate ?? 0; break;
        case 'fabric_width':      aVal = a.fabric_width ?? -1; bVal = b.fabric_width ?? -1; break;
        case 'last_purchase_date':
          aVal = a.last_purchase_date ? new Date(a.last_purchase_date).getTime() : 0;
          bVal = b.last_purchase_date ? new Date(b.last_purchase_date).getTime() : 0;
          break;
        default: return 0;
      }
      if (typeof aVal === 'string') return aVal.localeCompare(bVal) * dir;
      return (aVal - bVal) * dir;
    });
  }, [filteredInventory, tableSortCol, tableSortDir]);

  // Summary metrics for current view
  const summary = useMemo(() => {
    if (serverSummary) return serverSummary;
    const totalAvailable = filteredInventory.reduce((sum, it) => sum + (Number(it.available_qty) || 0), 0);
    const totalSkus = filteredInventory.length;
    const mainItems = filteredInventory.filter(it => !it.is_sample);
    const sampleItems = filteredInventory.filter(it => !!it.is_sample);
    const mainAvailable = mainItems.reduce((sum, it) => sum + (Number(it.available_qty) || 0), 0);
    const sampleAvailable = sampleItems.reduce((sum, it) => sum + (Number(it.available_qty) || 0), 0);
    return {
      totalAvailable,
      totalSkus,
      mainAvailable,
      mainSkus: mainItems.length,
      sampleAvailable,
      sampleSkus: sampleItems.length
    };
  }, [serverSummary, filteredInventory]);

  // Add function to fetch allocations for a material
  const fetchAllocations = async (materialId: number) => {
    try {
      setAllocationsLoading(true);
      const response = await api.get(`/design-materials/by-material/${materialId}`);
      setAllocations(response.data);
    } catch (err) {
      setAllocations([]);
    } finally {
      setAllocationsLoading(false);
    }
  };

  // Update handleViewMaterial to fetch allocations, adjustments, and full material details
  const handleViewMaterial = async (material: any) => {
    try {
      console.log('Opening material details for:', material);
      const response = await materialApi.getById(material.id);
      const fullMaterial = response.data;
      console.log('Full material data:', fullMaterial);
      setSelectedMaterial(fullMaterial);
      setPreviewDialogOpen(true);
      // Fetch allocations for this material
      fetchAllocations(fullMaterial.id);
      // Fetch adjustment history for this material
      fetchAdjustmentHistory(fullMaterial.id);
      // Fetch inventory change log for this material
      fetchChangeLog(fullMaterial.id);
    } catch (err) {
      console.error('Error fetching material details:', err);
    }
  };

  // Fetch adjustment history for preview dialog
  const fetchAdjustmentHistory = async (materialId: number) => {
    try {
      setAdjustmentHistoryLoading(true);
      const history = await inventoryAdjustmentApi.getByMaterial(materialId);
      setAdjustmentHistory(history);
    } catch (err) {
      console.error('Error fetching adjustment history:', err);
      setAdjustmentHistory([]);
    } finally {
      setAdjustmentHistoryLoading(false);
    }
  };

  // Fetch inventory change log for preview dialog
  const fetchChangeLog = async (materialId: number) => {
    try {
      setChangeLogLoading(true);
      const result = await inventoryChangeLogApi.getByMaterial(materialId, { pageSize: 100 });
      setChangeLog(result.data || []);
    } catch (err) {
      console.error('Error fetching inventory change log:', err);
      setChangeLog([]);
    } finally {
      setChangeLogLoading(false);
    }
  };

  // Helper to get change type display info
  const getChangeTypeInfo = (changeType: string): { label: string; color: 'error' | 'success' | 'warning' | 'info' | 'default' } => {
    switch (changeType) {
      case 'JW_ISSUE': return { label: 'JW Issue', color: 'error' };
      case 'JW_RECEIVE': return { label: 'JW Receive', color: 'success' };
      case 'JW_CANCEL': return { label: 'JW Cancel', color: 'warning' };
      case 'JW_DELETE': return { label: 'JW Delete', color: 'warning' };
      case 'JW_SHORTAGE': return { label: 'Shortage', color: 'error' };
      case 'PURCHASE': return { label: 'Purchase', color: 'success' };
      case 'ALLOCATION': return { label: 'Allocation', color: 'info' };
      case 'DEALLOCATION': return { label: 'Deallocation', color: 'info' };
      case 'ADJUSTMENT': return { label: 'Adjustment', color: 'default' };
      case 'RECEIPT_DELETE': return { label: 'Receipt Del', color: 'error' };
      default: return { label: changeType, color: 'default' };
    }
  };

  // Handle viewing purchase from material dialog
  const handleViewPurchase = (purchase: any) => {
    // Close material preview and navigate to Purchases page with state to open view dialog
    setPreviewDialogOpen(false);
    navigate('/materials', { state: { viewPurchaseId: purchase.id } });
  };

  // Update material image
  const updateMaterialImage = async (imageUrl: string) => {
    try {
      if (!selectedMaterial || !selectedMaterial.id) {
        console.error('No material selected or missing ID:', selectedMaterial);
        return;
      }

      console.log(`Updating material ID ${selectedMaterial.id} with new image URL`);

      const materialUpdateResponse = await api.patch(`/materials/${selectedMaterial.id}/image`, {
        image_url: imageUrl
      });

      console.log('Material update response:', materialUpdateResponse.data);

      const updatedMaterial = {
        ...selectedMaterial,
        image_url: imageUrl
      };
      setSelectedMaterial(updatedMaterial);

      // Update the material in the inventory array
      const updatedInventory = inventory.map((item: any) =>
        item.id === selectedMaterial.id ? { ...item, image_url: imageUrl } : item
      );
      setInventory(updatedInventory);

    } catch (err) {
      console.error('Error updating material image:', err);
    }
  };

  // Fetch all designs for allocation (paginated summary)
  const fetchDesigns = async () => {
    try {
      const pageSize = 200;
      let page = 1;
      let all: any[] = [];
      while (true) {
        const resp = await api.get('/designs', { params: { summary: 1, page, pageSize, withCount: 1 } });
        const data = Array.isArray(resp.data) ? resp.data : [];
        all = all.concat(data);
        const total = Number(resp.headers['x-total-count'] || 0);
        if (total > 0) {
          if (page * pageSize >= total) break;
        } else {
          if (data.length < pageSize) break;
        }
        page += 1;
      }
      setDesigns(all);
    } catch (err: any) {
      console.error('Failed to fetch designs:', err);
    }
  };

  // Handle opening allocation dialog
  const handleOpenAllocationDialog = (material: any) => {
    setSelectedMaterialForAllocation(material);
    setAllocationFormData({
      design_no: '',
      design_company_id: 0,
      material_id: material.id,
      garment_part: '',
      is_sample: material.is_sample || false,
      allocated_qty: material.available_qty || 0
    });
    setSelectedDesignForAllocation(null);
    setAllocationErrors({});
    setAllocationDialogOpen(true);
  };

  // Handle design change in allocation form
  const handleAllocationDesignChange = (event: any, newValue: any) => {
    if (newValue) {
      setSelectedDesignForAllocation(newValue);
      setAllocationFormData({
        ...allocationFormData,
        design_no: newValue.design_no,
        design_company_id: newValue.company_id
      });
    } else {
      setSelectedDesignForAllocation(null);
      setAllocationFormData({
        ...allocationFormData,
        design_no: '',
        design_company_id: 0
      });
    }
  };

  // Handle form input changes
  const handleAllocationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAllocationFormData({
      ...allocationFormData,
      [name]: name === 'allocated_qty' ? parseFloat(value) || 0 : value
    });
  };

  // Handle checkbox changes
  const handleAllocationCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setAllocationFormData({
      ...allocationFormData,
      [name]: checked
    });
  };

  // Handle select changes
  const handleAllocationSelectChange = (e: any) => {
    const { name, value } = e.target;
    setAllocationFormData({
      ...allocationFormData,
      [name]: value
    });
  };

  // Validate allocation form
  const validateAllocationForm = () => {
    const errors: { [key: string]: string } = {};

    if (!allocationFormData.design_no) {
      errors.design_no = 'Design is required';
    }

    if (!allocationFormData.material_id) {
      errors.material_id = 'Material is required';
    }

    if (!allocationFormData.garment_part) {
      errors.garment_part = 'Garment part is required';
    }

    if (!allocationFormData.allocated_qty || allocationFormData.allocated_qty <= 0) {
      errors.allocated_qty = 'Allocated quantity must be greater than 0';
    }

    if (selectedMaterialForAllocation && (allocationFormData.allocated_qty || 0) > selectedMaterialForAllocation.available_qty) {
      errors.allocated_qty = 'Cannot allocate more than available quantity';
    }

    setAllocationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle allocation form submission
  const handleAllocationSubmit = async () => {
    if (!validateAllocationForm()) {
      return;
    }

    try {
      setAllocationLoading(true);
      await designMaterialsApi.create(allocationFormData as CreateDesignMaterialDTO);
      setSuccessMessage('Material allocated successfully');
      setAllocationDialogOpen(false);

      // Refresh inventory to show updated available quantities
      fetchInventory(inventoryPage, inventoryPageSize);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to allocate material');
    } finally {
      setAllocationLoading(false);
    }
  };

  // Handle opening adjustment dialog
  const handleOpenAdjustmentDialog = async (material: any) => {
    setSelectedMaterialForAdjustment(material);
    setAdjustmentFormData({
      adjustment_type: AdjustmentType.STOCK_AUDIT,
      new_qty: material.available_qty || 0,
      reason: '',
      reference_no: ''
    });
    setAdjustmentDialogOpen(true);
    
    // Fetch adjustment history for this material
    setAdjustmentHistoryLoading(true);
    try {
      const history = await inventoryAdjustmentApi.getByMaterial(material.id);
      setAdjustmentHistory(history);
    } catch (err) {
      console.error('Error fetching adjustment history:', err);
      setAdjustmentHistory([]);
    } finally {
      setAdjustmentHistoryLoading(false);
    }
  };

  // Handle adjustment form submission
  const handleAdjustmentSubmit = async () => {
    if (!selectedMaterialForAdjustment) return;

    try {
      setAdjustmentLoading(true);
      await inventoryAdjustmentApi.create({
        material_id: selectedMaterialForAdjustment.id,
        adjustment_type: adjustmentFormData.adjustment_type,
        new_qty: adjustmentFormData.new_qty,
        reason: adjustmentFormData.reason || undefined,
        reference_no: adjustmentFormData.reference_no || undefined
      });
      
      setSuccessMessage('Inventory adjusted successfully');
      setAdjustmentDialogOpen(false);
      
      // Refresh inventory
      fetchInventory(inventoryPage, inventoryPageSize);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to adjust inventory');
    } finally {
      setAdjustmentLoading(false);
    }
  };

  // Get adjustment type label
  const getAdjustmentTypeLabel = (type: AdjustmentType) => {
    const labels: Record<AdjustmentType, string> = {
      RETURN: 'Return to Supplier',
      DAMAGED: 'Damaged/Defective',
      STOCK_AUDIT: 'Stock Audit',
      CORRECTION: 'Data Correction',
      OTHER: 'Other'
    };
    return labels[type] || type;
  };

  // Get adjustment type color
  const getAdjustmentTypeColor = (type: AdjustmentType): 'error' | 'warning' | 'info' | 'success' | 'default' => {
    const colors: Record<AdjustmentType, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
      RETURN: 'warning',
      DAMAGED: 'error',
      STOCK_AUDIT: 'info',
      CORRECTION: 'success',
      OTHER: 'default'
    };
    return colors[type] || 'default';
  };

  // ── Bulk selection handlers ────────────────────────────────────────────────
  const handleToggleSelect = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === sortedInventory.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedInventory.map(i => i.id)));
    }
  };

  const handleOpenBulkAdjust = () => {
    // Pre-fill qty map with each selected item's current available_qty
    const qtyMap: Record<number, string> = {};
    sortedInventory.filter(i => selectedIds.has(i.id)).forEach(item => {
      qtyMap[item.id] = String(item.available_qty ?? 0);
    });
    setBulkQtyMap(qtyMap);
    setBulkAdjustType(AdjustmentType.STOCK_AUDIT);
    setBulkAdjustReason('');
    setBulkAdjustRef('');
    setBulkAdjustResult(null);
    setBulkAdjustOpen(true);
  };

  const handleBulkSetZero = (id: number, checked: boolean) => {
    setBulkQtyMap(prev => ({ ...prev, [id]: checked ? '0' : String(sortedInventory.find(i => i.id === id)?.available_qty ?? 0) }));
  };

  const handleBulkSubmit = async () => {
    const selectedItems = sortedInventory.filter(i => selectedIds.has(i.id));
    const payload: CreateBulkAdjustmentDTO = {
      adjustment_type: bulkAdjustType,
      reason: bulkAdjustReason || undefined,
      reference_no: bulkAdjustRef || undefined,
      adjustments: selectedItems.map(item => ({
        material_id: item.id,
        new_qty: parseFloat(bulkQtyMap[item.id] ?? String(item.available_qty)) || 0,
      })),
    };
    try {
      setBulkAdjustLoading(true);
      const result = await inventoryAdjustmentApi.createBulk(payload);
      setBulkAdjustResult(result);
      // Refresh inventory
      setInventory([]);
      setInventoryPage(1);
      setHasMore(true);
      fetchInventory(1, inventoryPageSize);
      if (result.failed === 0) {
        setSuccessMessage(`Bulk adjusted ${result.success} item(s) successfully`);
        setBulkAdjustOpen(false);
        setSelectedIds(new Set());
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Bulk adjustment failed');
    } finally {
      setBulkAdjustLoading(false);
    }
  };

  // Open recalculate dialog and load preview
  const handleOpenRecalcDialog = async () => {
    setRecalcChanges([]);
    setRecalcDone(false);
    setRecalcDialogOpen(true);
    setRecalcPreviewLoading(true);
    try {
      const resp = await api.get('/materials/recalculate-inventory');
      setRecalcChanges(resp.data.changes || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load recalculation preview');
      setRecalcDialogOpen(false);
    } finally {
      setRecalcPreviewLoading(false);
    }
  };

  // Apply the recalculation
  const handleApplyRecalc = async () => {
    setRecalcApplyLoading(true);
    try {
      const resp = await api.post('/materials/recalculate-inventory');
      setRecalcDone(true);
      setRecalcChanges(resp.data.changes || []);
      setSuccessMessage(`Inventory recalculated: ${resp.data.updated} material(s) corrected.`);
      // Refresh inventory list
      setInventory([]);
      setInventoryPage(1);
      setHasMore(true);
      fetchInventory(1, inventoryPageSize);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to apply recalculation');
    } finally {
      setRecalcApplyLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Inventory Management
          </Typography>
          {isMaster && (
            <Tooltip title="Recalculate available quantities based on actual purchases, design allocations and job work issuances">
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleOpenRecalcDialog}
              >
                Recalculate Inventory
              </Button>
            </Tooltip>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {/* Summary Card - always shown */}
        <Box sx={{ mb: 2 }}>
          <Paper elevation={1} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Total Available Qty</Typography>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                  {summary.totalAvailable?.toLocaleString('en-IN') || '0'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">SKUs</Typography>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                  {summary.totalSkus?.toLocaleString('en-IN') || '0'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Available (Main / Sample)</Typography>
                <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
                  {(summary.mainAvailable || 0).toLocaleString('en-IN')} / {(summary.sampleAvailable || 0).toLocaleString('en-IN')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">SKUs (Main / Sample)</Typography>
                <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
                  {(summary.mainSkus || 0).toLocaleString('en-IN')} / {(summary.sampleSkus || 0).toLocaleString('en-IN')}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Filters - always shown */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            {/* Row 1: Search and Dropdowns */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search by Code, Type or Quality"
                variant="outlined"
                value={inventorySearch}
                onChange={(e) => setInventorySearch(e.target.value)}
                size="small"
                InputProps={{
                  endAdornment: inventorySearch && (
                    <IconButton
                      size="small"
                      onClick={() => setInventorySearch('')}
                      edge="end"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )
                }}
              />
            </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={[
                      { value: '', label: 'All Types' },
                      ...availableMaterialTypes.map(type => ({
                        value: type,
                        label: getMaterialTypeName(type)
                      }))
                    ]}
                    value={{ value: materialTypeFilter, label: getMaterialTypeName(materialTypeFilter) }}
                    onChange={(event, newValue) => {
                      setMaterialTypeFilter(newValue?.value || '');
                      setInventoryPage(1);
                    }}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Material Type"
                        size="small"
                      />
                    )}
                    disableClearable
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={[
                      { id: null, label: 'All Suppliers' },
                      ...availableVendors.map(vendor => ({
                        id: vendor.id,
                        label: vendor.alias || vendor.registered_name
                      }))
                    ]}
                    value={
                      vendorFilter
                        ? availableVendors.find(v => v.id === vendorFilter)
                          ? { id: vendorFilter, label: availableVendors.find(v => v.id === vendorFilter)?.alias || availableVendors.find(v => v.id === vendorFilter)?.registered_name || '' }
                          : { id: null, label: 'All Suppliers' }
                        : { id: null, label: 'All Suppliers' }
                    }
                    onChange={(event, newValue) => {
                      setVendorFilter(newValue?.id || null);
                      setInventoryPage(1);
                    }}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Supplier"
                        size="small"
                      />
                    )}
                    disableClearable
                  />
                </Grid>

                {/* Row 2: Sample Type Buttons and Checkboxes */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Button
                      variant={showSamplesOnly === null ? "contained" : "outlined"}
                      size="small"
                      onClick={() => { setShowSamplesOnly(null); setInventoryPage(1); }}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      All
                    </Button>
                    <Button
                      variant={showSamplesOnly === false ? "contained" : "outlined"}
                      size="small"
                      onClick={() => { setShowSamplesOnly(false); setInventoryPage(1); }}
                      sx={{ fontSize: '0.75rem', px: 1 }}
                    >
                      Main
                    </Button>
                    <Button
                      variant={showSamplesOnly === true ? "contained" : "outlined"}
                      size="small"
                      onClick={() => { setShowSamplesOnly(true); setInventoryPage(1); }}
                      sx={{ fontSize: '0.75rem', px: 1 }}
                    >
                      Samples
                    </Button>
                  </Box>
                </Grid>

                {/* Row 3: Checkboxes + View Toggle */}
                <Grid item xs={12} sm={6} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Checkbox
                        checked={availableQtyOnly}
                        onChange={e => { setAvailableQtyOnly(e.target.checked); setInventoryPage(1); }}
                        color="primary"
                        size="small"
                      />
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Available Only</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Checkbox
                        checked={unallocatedOnly}
                        onChange={e => { setUnallocatedOnly(e.target.checked); setInventoryPage(1); }}
                        color="primary"
                        size="small"
                      />
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Unallocated Only</Typography>
                    </Box>
                    <Tooltip title="Download filtered data as CSV">
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleDownloadCsv}
                          disabled={csvLoading || inventoryTotal === 0}
                          startIcon={csvLoading ? <CircularProgress size={14} color="inherit" /> : <FileDownloadIcon />}
                          sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
                        >
                          CSV
                        </Button>
                      </span>
                    </Tooltip>
                    {/* View mode toggle — desktop only */}
                    {!isMobile && (
                      <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        size="small"
                      >
                        <ToggleButton value="card" aria-label="Card view">
                          <Tooltip title="Card view">
                            <ViewModuleIcon fontSize="small" />
                          </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="table" aria-label="Table view">
                          <Tooltip title="Table view">
                            <ViewListIcon fontSize="small" />
                          </Tooltip>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>

        {/* Loading indicator */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredInventory.length > 0 ? (
          <>
            {isMobile ? (
              <Grid container spacing={2}>
                {filteredInventory.map((item) => (
                  <Grid item key={item.id || item.material_code} xs={6}>
                    <Card sx={{ borderRadius: 2, boxShadow: 1, overflow: 'hidden' }}>
                      {/* Image on top - taller to avoid cropping */}
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: 160,
                          bgcolor: 'background.default',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleViewMaterial(item)}
                      >
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={`Material ${item.material_code}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        ) : (
                          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                            <Typography variant="caption" color="text.secondary">No image</Typography>
                          </Box>
                        )}
                        <Box sx={{ position: 'absolute', top: 6, left: 6 }}>
                          {item.is_sample ? (
                            <Chip size="small" color="warning" label="Sample" />
                          ) : (
                            <Chip size="small" color="primary" label="Main" />
                          )}
                        </Box>
                      </Box>
                      {/* Details below image */}
                      <Box sx={{ p: 1.25 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {item.material_code}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {getMaterialTypeName(item.material_type)}{item.material_type === 'SEMI_FINISHED' ? '' : ` | ${item.material_quality || '-'}`}
                        </Typography>
                        {item.source_type === 'JOB_WORK' && item.design_no && (
                          <Typography variant="caption" color="primary" display="block">
                            {item.design_no} · {item.garment_part}{item.size ? ` · ${item.size}` : ''}
                          </Typography>
                        )}
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          {item.source_type === 'JOB_WORK' ? 'Total Qty' : 'Purchase Qty'}: <b>{item.quantity}</b>
                        </Typography>
                        <Typography variant="caption" display="block">
                          Allocated: <b>{item.allocated_qty}</b>
                        </Typography>
                        <Typography variant="caption" display="block">
                          Available: <b>{item.available_qty}</b>
                          {item.qc_hold && (
                            <Chip label="QC Pending" size="small" color="warning" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                          )}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Rate: ₹{item.rate?.toLocaleString('en-IN')}
                        </Typography>
                        {item.source_type !== 'JOB_WORK' && (
                          <>
                        <Typography variant="caption" display="block">
                          Fabric Width: {item.fabric_width ?? '-'}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Fabric L: {item.fabric_L ?? '-'}
                        </Typography>
                          </>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {item.source_type === 'JOB_WORK' ? 'From Job Work' : `Last Purchase: ${item.last_purchase_date ? formatDate(item.last_purchase_date) : '-'}`}
                        </Typography>
                        <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewMaterial(item)}
                              sx={{ bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.main', color: 'white' } }}
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                            <Tooltip title="Adjust Inventory">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleOpenAdjustmentDialog(item)}
                                sx={{ bgcolor: 'warning.light', '&:hover': { bgcolor: 'warning.main', color: 'white' } }}
                              >
                                <BuildIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {item.available_qty > 0 && (
                            <Tooltip title={item.qc_hold ? 'QC clearance required before allocation' : ''}>
                              <span>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<SendIcon />}
                                  onClick={() => handleOpenAllocationDialog(item)}
                                  disabled={!!item.qc_hold}
                                >
                                  Allocate
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : viewMode === 'table' ? (
              /* ── TABLE VIEW ── */
              <>
                {/* Bulk action bar — appears when rows are selected */}
                {selectedIds.size > 0 && (
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, mb: 1,
                    bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.300',
                    borderRadius: 2, position: 'sticky', top: 64, zIndex: 10,
                  }}>
                    <PlaylistAddCheckIcon color="warning" />
                    <Typography variant="body2" fontWeight={700}>
                      {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected
                    </Typography>
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      startIcon={<PlaylistAddCheckIcon />}
                      onClick={handleOpenBulkAdjust}
                    >
                      Adjust Inventory
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      color="inherit"
                      onClick={() => setSelectedIds(new Set())}
                      sx={{ ml: 'auto' }}
                    >
                      Clear selection
                    </Button>
                  </Box>
                )}
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {/* Select-all checkbox */}
                      <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper' }}>
                        <Checkbox
                          size="small"
                          indeterminate={selectedIds.size > 0 && selectedIds.size < sortedInventory.length}
                          checked={sortedInventory.length > 0 && selectedIds.size === sortedInventory.length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      {/* Thumbnail — not sortable */}
                      <TableCell sx={{ fontWeight: 700, minWidth: 40 }} />
                      {/* Sortable columns */}
                      {(
                        [
                          { id: 'material_code',     label: 'Code',          align: 'left',  minWidth: 120 },
                          { id: 'material_type',     label: 'Type',          align: 'left'  },
                          { id: 'material_quality',  label: 'Quality',       align: 'left'  },
                          { id: null,                label: 'Supplier',      align: 'left'  }, // not sortable
                          { id: null,                label: 'UOM',           align: 'left'  }, // not sortable
                          { id: 'quantity',          label: 'Purchase Qty',  align: 'right' },
                          { id: 'allocated_qty',     label: 'Allocated',     align: 'right' },
                          { id: 'available_qty',     label: 'Available',     align: 'right' },
                          { id: 'rate',              label: 'Rate (₹)',      align: 'right' },
                          { id: 'fabric_width',      label: 'Width',         align: 'left'  },
                          { id: 'last_purchase_date',label: 'Last Purchase', align: 'left'  },
                        ] as Array<{ id: SortCol | null; label: string; align: 'left' | 'right'; minWidth?: number }>
                      ).map(col => (
                        <TableCell
                          key={col.label}
                          align={col.align}
                          sx={{ fontWeight: 700, minWidth: col.minWidth, whiteSpace: 'nowrap' }}
                          sortDirection={col.id && tableSortCol === col.id ? tableSortDir : false}
                        >
                          {col.id ? (
                            <TableSortLabel
                              active={tableSortCol === col.id}
                              direction={tableSortCol === col.id ? tableSortDir : 'asc'}
                              onClick={() => handleTableSort(col.id as SortCol)}
                            >
                              {col.label}
                            </TableSortLabel>
                          ) : col.label}
                        </TableCell>
                      ))}
                      {/* Actions — not sortable */}
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedInventory.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        selected={selectedIds.has(item.id)}
                        sx={{ cursor: 'pointer', '&:last-child td': { border: 0 }, ...(item.qc_hold ? { bgcolor: 'warning.50' } : {}) }}
                      >
                        {/* Row checkbox */}
                        <TableCell padding="checkbox" onClick={(e) => handleToggleSelect(item.id, e)}>
                          <Checkbox size="small" checked={selectedIds.has(item.id)} />
                        </TableCell>
                        {/* Thumbnail — click opens lightbox */}
                        <TableCell sx={{ p: 0.5 }}>
                          {item.image_url ? (
                            <Tooltip title="View full image">
                              <Box
                                component="img"
                                src={item.image_url}
                                alt={item.material_code}
                                onClick={(e: React.MouseEvent) => { e.stopPropagation(); setLightboxUrl(item.image_url); }}
                                sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, display: 'block', cursor: 'zoom-in', transition: 'opacity 0.15s', '&:hover': { opacity: 0.8 } }}
                              />
                            </Tooltip>
                          ) : (
                            <Box
                              onClick={() => handleViewMaterial(item)}
                              sx={{ width: 40, height: 40, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            >
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>N/A</Typography>
                            </Box>
                          )}
                        </TableCell>
                        {/* Code */}
                        <TableCell onClick={() => handleViewMaterial(item)}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={700}>{item.material_code}</Typography>
                            {item.is_sample
                              ? <Chip size="small" color="warning" label="S" sx={{ height: 16, fontSize: '0.6rem' }} />
                              : <Chip size="small" color="primary" label="M" sx={{ height: 16, fontSize: '0.6rem' }} />
                            }
                          </Box>
                          {item.source_type === 'JOB_WORK' && item.design_no && (
                            <Typography variant="caption" color="primary" display="block">
                              {item.design_no}{item.garment_part ? ` · ${item.garment_part}` : ''}
                            </Typography>
                          )}
                        </TableCell>
                        {/* Type */}
                        <TableCell onClick={() => handleViewMaterial(item)}>
                          <Typography variant="body2">{getMaterialTypeName(item.material_type)}</Typography>
                        </TableCell>
                        {/* Quality */}
                        <TableCell onClick={() => handleViewMaterial(item)}>
                          <Typography variant="body2" color="text.secondary">{item.material_quality || '-'}</Typography>
                        </TableCell>
                        {/* Supplier */}
                        <TableCell onClick={() => handleViewMaterial(item)}>
                          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.source_type === 'JOB_WORK' ? 'Job Work' : (item.vendor?.alias || item.vendor?.registered_name || '-')}
                          </Typography>
                        </TableCell>
                        {/* UOM */}
                        <TableCell onClick={() => handleViewMaterial(item)}>
                          <Typography variant="body2">{item.uom}</Typography>
                        </TableCell>
                        {/* Purchase Qty */}
                        <TableCell align="right" onClick={() => handleViewMaterial(item)}>
                          <Typography variant="body2">{item.quantity?.toLocaleString('en-IN')}</Typography>
                        </TableCell>
                        {/* Allocated */}
                        <TableCell align="right" onClick={() => handleViewMaterial(item)}>
                          <Typography variant="body2" color={item.allocated_qty > 0 ? 'info.main' : 'text.secondary'}>
                            {item.allocated_qty?.toLocaleString('en-IN') ?? 0}
                          </Typography>
                        </TableCell>
                        {/* Available */}
                        <TableCell align="right" onClick={() => handleViewMaterial(item)}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            {item.qc_hold && (
                              <Chip label="QC" size="small" color="warning" sx={{ height: 18, fontSize: '0.6rem' }} />
                            )}
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color={item.qc_hold ? 'warning.main' : item.available_qty <= 0 ? 'error.main' : item.available_qty < 50 ? 'warning.main' : 'success.main'}
                            >
                              {item.available_qty?.toLocaleString('en-IN') ?? 0}
                            </Typography>
                          </Box>
                        </TableCell>
                        {/* Rate */}
                        <TableCell align="right" onClick={() => handleViewMaterial(item)}>
                          <Typography variant="body2">₹{item.rate?.toLocaleString('en-IN')}</Typography>
                        </TableCell>
                        {/* Width */}
                        <TableCell onClick={() => handleViewMaterial(item)}>
                          <Typography variant="body2" color="text.secondary">
                            {item.source_type === 'JOB_WORK' ? '-' : (item.fabric_width ?? '-')}
                          </Typography>
                        </TableCell>
                        {/* Last Purchase */}
                        <TableCell onClick={() => handleViewMaterial(item)}>
                          <Typography variant="body2" color="text.secondary">
                            {item.source_type === 'JOB_WORK' ? 'JW' : (item.last_purchase_date ? formatDate(item.last_purchase_date) : '-')}
                          </Typography>
                        </TableCell>
                        {/* Actions */}
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="View details">
                              <IconButton size="small" color="primary" onClick={() => handleViewMaterial(item)}>
                                <LaunchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Adjust inventory">
                              <IconButton size="small" color="warning" onClick={() => handleOpenAdjustmentDialog(item)}>
                                <BuildIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {item.available_qty > 0 && (
                              <Tooltip title={item.qc_hold ? 'QC clearance required before allocation' : 'Allocate to design'}>
                                <span>
                                  <IconButton size="small" color="success" onClick={() => handleOpenAllocationDialog(item)} disabled={!!item.qc_hold}>
                                    <SendIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={inventoryTotal}
                page={inventoryPage - 1}  // MUI is 0-indexed, our state is 1-indexed
                rowsPerPage={inventoryPageSize}
                rowsPerPageOptions={[25, 50, 100]}
                onPageChange={(_, newPage) => {
                  setInventoryPage(newPage + 1);
                  setSelectedIds(new Set());
                }}
                onRowsPerPageChange={(e) => {
                  setInventoryPageSize(parseInt(e.target.value, 10));
                  setInventoryPage(1);
                  setSelectedIds(new Set());
                }}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
              />
              </>
            ) : (
              /* ── CARD VIEW ── */
              <Box>
                <Grid container spacing={2}>
                  {filteredInventory.map((item) => (
                    <Grid item key={item.id} xs={12} sm={6} md={4} lg={3}>
                      <Card sx={{ borderRadius: 2, boxShadow: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            height: 220,
                            bgcolor: 'background.default',
                            overflow: 'hidden',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleViewMaterial(item)}
                        >
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={`Material ${item.material_code}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                              <Typography variant="caption" color="text.secondary">No image</Typography>
                            </Box>
                          )}
                          {/* Top overlay tags */}
                          <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 1 }}>
                            {item.is_sample ? (
                              <Chip size="small" color="warning" label="Sample" />
                            ) : (
                              <Chip size="small" color="primary" label="Main" />
                            )}
                          </Box>
                        </Box>
                        <Card sx={{ boxShadow: 'none' }}>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {item.material_code}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {getMaterialTypeName(item.material_type)} | {item.material_quality || '-'}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              Purchase Qty: <b>{item.quantity}</b> | Allocated: <b>{item.allocated_qty}</b> | Available: <b>{item.available_qty}</b>
                            </Typography>
                            <Typography variant="body2">
                              UOM: {item.uom} | Rate: ₹{item.rate?.toLocaleString('en-IN')}
                            </Typography>
                            <Typography variant="body2">
                              Fabric Width: {item.fabric_width ?? '-'} | Fabric L: {item.fabric_L ?? '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Last Purchase: {item.last_purchase_date ? formatDate(item.last_purchase_date) : '-'}
                            </Typography>
                          </Box>
                          <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewMaterial(item)}
                                sx={{ bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.main', color: 'white' } }}
                              >
                                <LaunchIcon fontSize="small" />
                              </IconButton>
                              <Tooltip title="Adjust Inventory">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleOpenAdjustmentDialog(item)}
                                  sx={{ bgcolor: 'warning.light', '&:hover': { bgcolor: 'warning.main', color: 'white' } }}
                                >
                                  <BuildIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            {item.available_qty > 0 && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SendIcon />}
                                onClick={() => handleOpenAllocationDialog(item)}
                              >
                                Allocate
                              </Button>
                            )}
                          </Box>
                        </Card>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Infinite scroll sentinel — card view only */}
            {viewMode === 'card' && (
              <>
                <div ref={sentinelRef} style={{ height: 1 }} />
                {loadingMore && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={28} />
                  </Box>
                )}
                {!hasMore && inventory.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Showing all {inventory.length} of {inventoryTotal} items
                    </Typography>
                  </Box>
                )}
              </>
            )}

          </>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">
              {inventorySearch || materialTypeFilter || vendorFilter || showSamplesOnly !== null
                ? 'No results match your search or filter criteria. Try adjusting your filters.'
                : 'No inventory items found. Add materials through purchases to see inventory.'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Image Lightbox */}
      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth="lg"
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'hidden' } }}
        onClick={() => setLightboxUrl(null)}
      >
        {lightboxUrl && (
          <Box
            component="img"
            src={lightboxUrl}
            alt="Material image"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 2,
              display: 'block',
              boxShadow: 8
            }}
          />
        )}
      </Dialog>

      {/* Material Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedMaterial && (
          <>
            <DialogTitle>
              Material Details
              <IconButton
                onClick={() => setPreviewDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <DeleteIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {selectedMaterial.material_code}
                      {selectedMaterial.is_sample && (
                        <Chip size="small" color="warning" label="Sample" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    {/* Source Info - Semi-finished or Purchase */}
                    {selectedMaterial.source_type === 'JOB_WORK' ? (
                      <>
                        <Chip size="small" color="info" label="Semi Finished · From Job Work" sx={{ mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          <strong>Design:</strong> {selectedMaterial.design_no || '-'}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          <strong>Garment Part:</strong> {selectedMaterial.garment_part || '-'}
                        </Typography>
                        {selectedMaterial.size && (
                          <Typography variant="body1" gutterBottom>
                            <strong>Size:</strong> {selectedMaterial.size}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        {selectedMaterial.purchase && (
                          <>
                            <Typography variant="body1" gutterBottom>
                              <strong>Vendor:</strong> {selectedMaterial.purchase.vendor?.registered_name || '-'}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              <strong>Purchase:</strong>{' '}
                              {selectedMaterial.purchase.invoice_id ? (
                                <Button
                                  variant="text"
                                  size="small"
                                  sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                                  onClick={() => {
                                    setPreviewDialogOpen(false);
                                    handleViewPurchase(selectedMaterial.purchase);
                                  }}
                                >
                                  {selectedMaterial.purchase.invoice_id}
                                </Button>
                              ) : (
                                '-'
                              )}
                            </Typography>
                          </>
                        )}
                      </>
                    )}
                    <Typography variant="body1" gutterBottom>
                      <strong>Type:</strong> {getMaterialTypeName(selectedMaterial.material_type)}
                    </Typography>
                    {selectedMaterial.source_type !== 'JOB_WORK' && (
                      <Typography variant="body1" gutterBottom>
                        <strong>Quality:</strong> {selectedMaterial.material_quality || '-'}
                      </Typography>
                    )}
                    <Typography variant="body1" gutterBottom>
                      <strong>Available Quantity:</strong> {selectedMaterial.available_qty} {selectedMaterial.uom}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>Rate:</strong> ₹{selectedMaterial.rate?.toLocaleString('en-IN')}/{selectedMaterial.uom}
                    </Typography>
                    {selectedMaterial.source_type !== 'JOB_WORK' && (
                      <>
                        <Typography variant="body1" gutterBottom>
                          <strong>Last Purchase Date:</strong> {selectedMaterial.last_purchase_date ? formatDate(selectedMaterial.last_purchase_date) : '-'}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          <strong>Fabric Width:</strong> {selectedMaterial.fabric_width ?? '-'}
                        </Typography>
                      </>
                    )}
                    <Typography variant="body1" gutterBottom>
                      <strong>Fabric L:</strong> {selectedMaterial.fabric_L ?? '-'}
                    </Typography>
                  </Box>
                  {/* Allocations Table */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Design Allocations</Typography>
                    {allocationsLoading ? (
                      <CircularProgress size={24} />
                    ) : allocations.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">No allocations found for this material.</Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Design Image</TableCell>
                            <TableCell>Design No</TableCell>
                            <TableCell>Allocated Date</TableCell>
                            <TableCell align="right">Allocated Qty</TableCell>
                            <TableCell>Job Works</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {allocations.map((alloc, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                {alloc.design_image ? (
                                  <Box component="img" src={alloc.design_image} alt={alloc.design_no} sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }} />
                                ) : (
                                  <Box sx={{ width: 40, height: 40, bgcolor: '#eee', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>No Image</Box>
                                )}
                              </TableCell>
                              <TableCell>{alloc.design_no}</TableCell>
                              <TableCell>{alloc.allocated_date ? formatDate(alloc.allocated_date) : '-'}</TableCell>
                              <TableCell align="right">{alloc.allocated_qty}</TableCell>
                              <TableCell>
                                {alloc.job_works && alloc.job_works.length > 0 ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {alloc.job_works.map((jw: any) => (
                                      <Chip
                                        key={jw.id}
                                        label={`${jw.code} (${jw.quantity})`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        onClick={() => window.open(`/job-works/${jw.id}`, '_blank')}
                                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
                                      />
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">No Job Works</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </Box>

                  {/* Inventory Adjustments Table */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BuildIcon fontSize="small" color="warning" />
                      Inventory Adjustments
                    </Typography>
                    {adjustmentHistoryLoading ? (
                      <CircularProgress size={24} />
                    ) : adjustmentHistory.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">No adjustments found for this material.</Typography>
                    ) : (
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell align="right">Change</TableCell>
                              <TableCell align="right">New Qty</TableCell>
                              <TableCell>By</TableCell>
                              <TableCell>Reason</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {adjustmentHistory.map((adj) => (
                              <TableRow key={adj.id}>
                                <TableCell>{formatDate(adj.adjusted_at)}</TableCell>
                                <TableCell>
                                  <Chip 
                                    size="small" 
                                    label={getAdjustmentTypeLabel(adj.adjustment_type)} 
                                    color={getAdjustmentTypeColor(adj.adjustment_type)}
                                  />
                                </TableCell>
                                <TableCell 
                                  align="right"
                                  sx={{ 
                                    color: adj.quantity_change < 0 ? 'error.main' : adj.quantity_change > 0 ? 'success.main' : 'text.primary',
                                    fontWeight: 600
                                  }}
                                >
                                  {adj.quantity_change > 0 ? '+' : ''}{adj.quantity_change.toFixed(2)}
                                </TableCell>
                                <TableCell align="right">{adj.new_qty.toFixed(2)}</TableCell>
                                <TableCell>{adj.adjusted_by}</TableCell>
                                <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  <Tooltip title={adj.reason || '-'}>
                                    <span>{adj.reason || '-'}</span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>

                  {/* Inventory Change Log */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      📋 Inventory Change Log
                    </Typography>
                    {changeLogLoading ? (
                      <CircularProgress size={24} />
                    ) : changeLog.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">No change log entries for this material.</Typography>
                    ) : (
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Reference</TableCell>
                              <TableCell>Design</TableCell>
                              <TableCell align="right">Change</TableCell>
                              <TableCell align="right">After</TableCell>
                              <TableCell>Notes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {changeLog.map((log) => {
                              const typeInfo = getChangeTypeInfo(log.change_type);
                              return (
                                <TableRow key={log.id}>
                                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                                    {formatDate(log.effective_date)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip size="small" label={typeInfo.label} color={typeInfo.color} variant="outlined" />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    {log.reference_code ? (
                                      log.reference_type === 'JOB_WORK' ? (
                                        <Button
                                          size="small"
                                          variant="text"
                                          sx={{ textTransform: 'none', p: 0, minWidth: 0, fontSize: '0.75rem' }}
                                          onClick={() => window.open(`/job-works/${log.reference_id}`, '_blank')}
                                        >
                                          {log.reference_code}
                                        </Button>
                                      ) : (
                                        log.reference_code
                                      )
                                    ) : '-'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>{log.design_no || '-'}</TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      color: log.quantity_change < 0 ? 'error.main' : log.quantity_change > 0 ? 'success.main' : 'text.secondary',
                                      fontWeight: 600,
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    {log.quantity_change > 0 ? '+' : ''}{log.quantity_change.toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{log.new_qty.toFixed(2)}</TableCell>
                                  <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                                    <Tooltip title={log.notes || '-'}>
                                      <span>{log.notes || '-'}</span>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  {selectedMaterial.image_url ? (
                    <Box
                      sx={{
                        width: '100%',
                        height: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid #eee',
                      }}
                    >
                      <Box
                        component="img"
                        src={selectedMaterial.image_url}
                        alt={`Material ${selectedMaterial.material_code}`}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1,
                        border: '1px solid #eee',
                        bgcolor: '#f9f9f9',
                      }}
                    >
                      <Typography color="textSecondary">No image available</Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Material Allocation Dialog */}
      <Dialog open={allocationDialogOpen} onClose={() => setAllocationDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Allocate Material to Design</DialogTitle>
        <DialogContent>
          {selectedMaterialForAllocation && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Material: <strong>{selectedMaterialForAllocation.material_code}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: {getMaterialTypeName(selectedMaterialForAllocation.material_type)} |
                Available: {selectedMaterialForAllocation.available_qty} {selectedMaterialForAllocation.uom} |
                Rate: ₹{selectedMaterialForAllocation.rate}
              </Typography>
            </Box>
          )}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Design Selection */}
            <Grid item xs={12}>
              <Autocomplete
                options={designs}
                getOptionLabel={(option) => `${option.design_no} - ${option.name || 'Unnamed'}`}
                value={selectedDesignForAllocation}
                onChange={handleAllocationDesignChange}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      {option.image_url && (
                        <Box
                          component="img"
                          src={option.image_url}
                          alt={option.design_no}
                          sx={{ width: 40, height: 40, mr: 2, objectFit: 'cover', borderRadius: 1 }}
                        />
                      )}
                      <Typography>{option.design_no} - {option.name || 'Unnamed'}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Design"
                    error={!!allocationErrors.design_no}
                    helperText={allocationErrors.design_no}
                    required
                  />
                )}
              />
            </Grid>

            {/* Garment Part */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!allocationErrors.garment_part} required>
                <InputLabel>Garment Part</InputLabel>
                <Select
                  name="garment_part"
                  value={allocationFormData.garment_part || ''}
                  onChange={handleAllocationSelectChange}
                  label="Garment Part"
                >
                  {GARMENT_PARTS.map((part: GarmentPart) => (
                    <MenuItem key={part} value={part}>
                      {part}
                    </MenuItem>
                  ))}
                </Select>
                {allocationErrors.garment_part && (
                  <FormHelperText>{allocationErrors.garment_part}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Allocated Quantity */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Allocated Quantity"
                name="allocated_qty"
                type="number"
                value={allocationFormData.allocated_qty || ''}
                onChange={handleAllocationInputChange}
                InputProps={{
                  endAdornment: selectedMaterialForAllocation && (
                    <InputAdornment position="end">
                      {selectedMaterialForAllocation.uom}
                    </InputAdornment>
                  )
                }}
                error={!!allocationErrors.allocated_qty}
                helperText={allocationErrors.allocated_qty || `Max: ${selectedMaterialForAllocation?.available_qty || 0}`}
                required
              />
            </Grid>

            {/* Is Sample */}
            <Grid item xs={12}>
              <FormControl>
                <label
                  htmlFor="is_sample_allocation"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="checkbox"
                    id="is_sample_allocation"
                    name="is_sample"
                    checked={!!allocationFormData.is_sample}
                    onChange={handleAllocationCheckboxChange}
                    style={{ marginRight: '8px' }}
                  />
                  Sample Material (Not for production)
                </label>
              </FormControl>
            </Grid>

            {/* Material Information Display */}
            {selectedMaterialForAllocation && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Material Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        Available Quantity: <strong>{selectedMaterialForAllocation.available_qty} {selectedMaterialForAllocation.uom}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        Rate: <strong>₹{selectedMaterialForAllocation.rate} per {selectedMaterialForAllocation.uom}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        Quality: <strong>{selectedMaterialForAllocation.material_quality || '-'}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        Current Sample Status: <strong>{selectedMaterialForAllocation.is_sample ? 'Sample' : 'Main'}</strong>
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllocationDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAllocationSubmit}
            variant="contained"
            disabled={allocationLoading}
          >
            {allocationLoading ? <CircularProgress size={20} /> : 'Allocate Material'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recalculate Inventory Dialog (MASTER only) */}
      <Dialog
        open={recalcDialogOpen}
        onClose={() => !recalcApplyLoading && setRecalcDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RefreshIcon />
            Recalculate Available Inventory
          </Box>
        </DialogTitle>
        <DialogContent>
          {recalcPreviewLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
              <CircularProgress />
              <Typography color="text.secondary">Analysing inventory...</Typography>
            </Box>
          ) : recalcDone ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="success.main" gutterBottom>
                Inventory Recalculated Successfully
              </Typography>
              <Typography color="text.secondary">
                {recalcChanges.length} material(s) were corrected.
              </Typography>
            </Box>
          ) : (
            <>
              {recalcChanges.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="success.main">All inventory values are correct</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No discrepancies found between stored and calculated available quantities.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 2, p: 1.5, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                    <WarningIcon color="warning" />
                    <Typography variant="body2">
                      <strong>{recalcChanges.length} material(s)</strong> have incorrect available quantities.
                      Review the changes below and confirm to apply.
                    </Typography>
                  </Box>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Code</TableCell>
                          <TableCell>Quality</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Total Inward</TableCell>
                          <TableCell align="right">Design Alloc</TableCell>
                          <TableCell align="right">JW Issued</TableCell>
                          <TableCell align="right">JW Restored</TableCell>
                          <TableCell align="right">Adj Net</TableCell>
                          <TableCell align="right">Current Avail</TableCell>
                          <TableCell align="right">Correct Avail</TableCell>
                          <TableCell align="right">Diff</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recalcChanges.map((c: any) => (
                          <TableRow key={c.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{c.material_code}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{c.material_quality || '-'}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{getMaterialTypeName(c.material_type)}</TableCell>
                            {/* Total purchased quantity — the immutable base */}
                            <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                              {(c.total_quantity ?? 0).toFixed(2)}
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                {c.uom}
                              </Typography>
                            </TableCell>
                            {/* Remaining design allocations (pending, not yet in JW) */}
                            <TableCell align="right" sx={{ fontSize: '0.8rem', color: c.design_allocated > 0 ? 'info.main' : 'text.secondary' }}>
                              {(c.design_allocated ?? 0).toFixed(2)}
                            </TableCell>
                            {/* Actual inventory deducted by JW issues (remainder only) */}
                            <TableCell align="right" sx={{ fontSize: '0.8rem', color: c.jw_issued_from_inventory > 0 ? 'error.main' : 'text.secondary' }}>
                              {(c.jw_issued_from_inventory ?? 0).toFixed(2)}
                            </TableCell>
                            {/* Quantity restored via JW cancel/delete */}
                            <TableCell align="right" sx={{ fontSize: '0.8rem', color: c.jw_restored > 0 ? 'success.main' : 'text.secondary' }}>
                              {(c.jw_restored ?? 0).toFixed(2)}
                            </TableCell>
                            {/* Net manual adjustments */}
                            <TableCell align="right" sx={{ fontSize: '0.8rem', color: c.adjustment_net !== 0 ? (c.adjustment_net > 0 ? 'success.main' : 'error.main') : 'text.secondary' }}>
                              {c.adjustment_net > 0 ? '+' : ''}{(c.adjustment_net ?? 0).toFixed(2)}
                            </TableCell>
                            {/* Stored available_qty (may be wrong) */}
                            <TableCell align="right" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                              {c.current_available.toFixed(2)}
                            </TableCell>
                            {/* Calculated correct value */}
                            <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
                              {c.correct_available.toFixed(2)}
                            </TableCell>
                            {/* Delta — red if phantom inventory removed, green if under-counted */}
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                color: c.diff > 0 ? 'success.main' : 'error.main'
                              }}
                            >
                              {c.diff > 0 ? '+' : ''}{c.diff.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {/* Verification legend */}
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
                      How Correct Avail is calculated:
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      <strong>Correct Avail</strong> = Total Inward − Design Alloc − JW Issued + JW Restored + Adj Net
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      <span style={{ color: '#d32f2f' }}>■</span> JW Issued = only the portion that came from stock (not from design allocation)
                      &nbsp;&nbsp;
                      <span style={{ color: '#1976d2' }}>■</span> Design Alloc = reserved but not yet issued to any JW
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecalcDialogOpen(false)} disabled={recalcApplyLoading}>
            {recalcDone ? 'Close' : 'Cancel'}
          </Button>
          {!recalcDone && recalcChanges.length > 0 && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleApplyRecalc}
              disabled={recalcApplyLoading}
              startIcon={recalcApplyLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            >
              {recalcApplyLoading ? 'Applying...' : `Apply ${recalcChanges.length} Correction(s)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Inventory Adjustment Dialog */}
      <Dialog
        open={adjustmentDialogOpen}
        onClose={() => setAdjustmentDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon />
            Adjust Inventory
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedMaterialForAdjustment && (
            <>
              {/* Material Info Header */}
              <Box sx={{ mb: 3, mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    {selectedMaterialForAdjustment.image_url ? (
                      <Box
                        component="img"
                        src={selectedMaterialForAdjustment.image_url}
                        alt={selectedMaterialForAdjustment.material_code}
                        sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1 }}
                      />
                    ) : (
                      <Box sx={{ width: 60, height: 60, bgcolor: '#eee', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" color="text.secondary">No img</Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h6">{selectedMaterialForAdjustment.material_code}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getMaterialTypeName(selectedMaterialForAdjustment.material_type)} | {selectedMaterialForAdjustment.material_quality || '-'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Current Available:</strong> {selectedMaterialForAdjustment.available_qty} {selectedMaterialForAdjustment.uom}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Adjustment Form */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Adjustment Reason</InputLabel>
                    <Select
                      value={adjustmentFormData.adjustment_type}
                      onChange={(e) => setAdjustmentFormData({ ...adjustmentFormData, adjustment_type: e.target.value as AdjustmentType })}
                      label="Adjustment Reason"
                    >
                      <MenuItem value="STOCK_AUDIT">Stock Audit - Physical count mismatch</MenuItem>
                      <MenuItem value="RETURN">Return to Supplier</MenuItem>
                      <MenuItem value="DAMAGED">Damaged / Defective</MenuItem>
                      <MenuItem value="CORRECTION">Data Correction</MenuItem>
                      <MenuItem value="OTHER">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="New Available Quantity"
                    type="number"
                    value={adjustmentFormData.new_qty}
                    onChange={(e) => setAdjustmentFormData({ ...adjustmentFormData, new_qty: parseFloat(e.target.value) || 0 })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{selectedMaterialForAdjustment.uom}</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 }
                    }}
                    required
                    helperText={
                      adjustmentFormData.new_qty !== selectedMaterialForAdjustment.available_qty
                        ? `Change: ${adjustmentFormData.new_qty - selectedMaterialForAdjustment.available_qty > 0 ? '+' : ''}${(adjustmentFormData.new_qty - selectedMaterialForAdjustment.available_qty).toFixed(2)} ${selectedMaterialForAdjustment.uom}`
                        : 'No change'
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Reference No (Optional)"
                    value={adjustmentFormData.reference_no}
                    onChange={(e) => setAdjustmentFormData({ ...adjustmentFormData, reference_no: e.target.value })}
                    placeholder="e.g., Return Invoice, Audit Ref"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Notes / Reason Details"
                    value={adjustmentFormData.reason}
                    onChange={(e) => setAdjustmentFormData({ ...adjustmentFormData, reason: e.target.value })}
                    multiline
                    rows={2}
                    placeholder="Describe why this adjustment is needed"
                  />
                </Grid>

                {/* Summary of change */}
                <Grid item xs={12}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      bgcolor: adjustmentFormData.new_qty < selectedMaterialForAdjustment.available_qty 
                        ? 'error.50' 
                        : adjustmentFormData.new_qty > selectedMaterialForAdjustment.available_qty 
                          ? 'success.50' 
                          : 'grey.100',
                      borderColor: adjustmentFormData.new_qty < selectedMaterialForAdjustment.available_qty 
                        ? 'error.main' 
                        : adjustmentFormData.new_qty > selectedMaterialForAdjustment.available_qty 
                          ? 'success.main' 
                          : 'grey.300'
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={4} sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Before</Typography>
                        <Typography variant="h6">{selectedMaterialForAdjustment.available_qty}</Typography>
                      </Grid>
                      <Grid item xs={4} sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Change</Typography>
                        <Typography 
                          variant="h6" 
                          color={
                            adjustmentFormData.new_qty < selectedMaterialForAdjustment.available_qty 
                              ? 'error.main' 
                              : adjustmentFormData.new_qty > selectedMaterialForAdjustment.available_qty 
                                ? 'success.main' 
                                : 'text.primary'
                          }
                        >
                          {adjustmentFormData.new_qty - selectedMaterialForAdjustment.available_qty > 0 ? '+' : ''}
                          {(adjustmentFormData.new_qty - selectedMaterialForAdjustment.available_qty).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4} sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">After</Typography>
                        <Typography variant="h6">{adjustmentFormData.new_qty}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>

              {/* Adjustment History */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Adjustment History
                </Typography>
                {adjustmentHistoryLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : adjustmentHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No previous adjustments for this material.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Change</TableCell>
                          <TableCell align="right">New Qty</TableCell>
                          <TableCell>By</TableCell>
                          <TableCell>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {adjustmentHistory.map((adj) => (
                          <TableRow key={adj.id}>
                            <TableCell>{formatDate(adj.adjusted_at)}</TableCell>
                            <TableCell>
                              <Chip 
                                size="small" 
                                label={getAdjustmentTypeLabel(adj.adjustment_type)} 
                                color={getAdjustmentTypeColor(adj.adjustment_type)}
                              />
                            </TableCell>
                            <TableCell 
                              align="right"
                              sx={{ 
                                color: adj.quantity_change < 0 ? 'error.main' : adj.quantity_change > 0 ? 'success.main' : 'text.primary',
                                fontWeight: 600
                              }}
                            >
                              {adj.quantity_change > 0 ? '+' : ''}{adj.quantity_change.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">{adj.new_qty.toFixed(2)}</TableCell>
                            <TableCell>{adj.adjusted_by}</TableCell>
                            <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <Tooltip title={adj.reason || '-'}>
                                <span>{adj.reason || '-'}</span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustmentDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAdjustmentSubmit}
            variant="contained"
            color="warning"
            disabled={adjustmentLoading || (adjustmentFormData.new_qty === selectedMaterialForAdjustment?.available_qty)}
          >
            {adjustmentLoading ? <CircularProgress size={20} /> : 'Apply Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Inventory Adjustment Dialog ─────────────────────────── */}
      <Dialog open={bulkAdjustOpen} onClose={() => !bulkAdjustLoading && setBulkAdjustOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.light', display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          <PlaylistAddCheckIcon />
          Bulk Adjust Inventory — {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {/* Shared settings */}
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Adjustment Type</InputLabel>
                  <Select
                    value={bulkAdjustType}
                    label="Adjustment Type"
                    onChange={e => setBulkAdjustType(e.target.value as AdjustmentType)}
                  >
                    {Object.values(AdjustmentType).map(t => (
                      <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth size="small" label="Reason"
                  value={bulkAdjustReason}
                  onChange={e => setBulkAdjustReason(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth size="small" label="Reference No"
                  value={bulkAdjustRef}
                  onChange={e => setBulkAdjustRef(e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Per-material quantity table */}
          <Box sx={{ px: 2, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 'auto' }}>
              Set new available quantity for each material:
            </Typography>
            <Button
              size="small"
              color="error"
              variant="outlined"
              sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
              onClick={() => {
                const zeroed: Record<number, string> = {};
                sortedInventory.filter(i => selectedIds.has(i.id)).forEach(item => { zeroed[item.id] = '0'; });
                setBulkQtyMap(zeroed);
              }}
            >
              Set all to 0
            </Button>
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
              onClick={() => {
                const reset: Record<number, string> = {};
                sortedInventory.filter(i => selectedIds.has(i.id)).forEach(item => { reset[item.id] = String(item.available_qty ?? 0); });
                setBulkQtyMap(reset);
              }}
            >
              Reset all
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, minWidth: 110 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Quality</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, minWidth: 90 }}>Current Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>New Qty</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, width: 70 }}>Set 0</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedInventory.filter(i => selectedIds.has(i.id)).map(item => {
                  const currentVal = bulkQtyMap[item.id] ?? String(item.available_qty ?? 0);
                  const newQty = parseFloat(currentVal) || 0;
                  const delta = newQty - (item.available_qty ?? 0);
                  const isZero = currentVal === '0';
                  return (
                    <TableRow key={item.id} selected={isZero}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{item.material_code}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.uom}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{getMaterialTypeName(item.material_type)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{item.material_quality || '—'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.disabled">{item.available_qty?.toLocaleString('en-IN')}</Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={currentVal}
                          disabled={isZero}
                          onChange={e => setBulkQtyMap(prev => ({ ...prev, [item.id]: e.target.value }))}
                          inputProps={{ min: 0, step: 0.5 }}
                          sx={{ width: 110 }}
                          helperText={
                            delta !== 0
                              ? <Typography component="span" variant="caption"
                                  sx={{ color: delta > 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                                  {delta > 0 ? '+' : ''}{delta.toFixed(delta % 1 === 0 ? 0 : 2)} {item.uom}
                                </Typography>
                              : undefined
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={isZero}
                          onChange={e => handleBulkSetZero(item.id, e.target.checked)}
                          color="error"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Result panel — shown after partial failures */}
          {bulkAdjustResult && bulkAdjustResult.failed > 0 && (
            <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Alert severity="warning" sx={{ mb: 1 }}>
                {bulkAdjustResult.success} item(s) adjusted successfully, {bulkAdjustResult.failed} failed.
              </Alert>
              {bulkAdjustResult.results.filter(r => r.status === 'error').map(r => (
                <Box key={r.material_id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                  <Chip label={r.material_code || `ID ${r.material_id}`} size="small" color="error" variant="outlined" />
                  <Typography variant="caption" color="error.main">{r.error}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => { setBulkAdjustOpen(false); if (bulkAdjustResult?.success) setSelectedIds(new Set()); }} disabled={bulkAdjustLoading}>
            {bulkAdjustResult?.failed ? 'Close' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={bulkAdjustLoading ? <CircularProgress size={16} color="inherit" /> : <PlaylistAddCheckIcon />}
            onClick={handleBulkSubmit}
            disabled={bulkAdjustLoading || selectedIds.size === 0}
          >
            {bulkAdjustLoading ? 'Adjusting…' : `Adjust ${selectedIds.size} Item${selectedIds.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InventoryPage;